<div class="grid grid-cols-1 lg:grid-cols-3 gap-6 w-full">

    <div class="lg:col-span-2 flex flex-col gap-6">

        <mat-card appearance="outlined">
            <mat-card-header>
                <mat-card-title>Basic Information</mat-card-title>
                <mat-card-subtitle>Core identifying details for the destination.</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content class="flex flex-col gap-4 pt-4">
                <mat-form-field appearance="outline" class="w-full">
                    <mat-label>
                        Name
                        <span *ngIf="isMissing(destination.ds_label)" class="text-red-500 font-bold ml-1 animate-pulse">⚠️ Missing Critical Data</span>
                    </mat-label>
                    <input matInput maxlength="150" [readonly]="reviewMode" [ngModel]="destination.ds_label" (ngModelChange)="onLabelChange($event)" (keyup)="onKeyupAutosave()"/>
                    <button mat-icon-button matSuffix [matTooltip]="'The primary, public-facing name of the destination.'">
                        <mat-icon svgIcon="heroicons_outline:question-mark-circle"></mat-icon>
                    </button>
                </mat-form-field>

                <mat-form-field *ngIf="userUiType !== 4" appearance="outline" class="w-full">
                    <mat-label>Internal Use Name</mat-label>
                    <input matInput maxlength="150" [readonly]="reviewMode" [(ngModel)]="destination.ds_name" (keyup)="onKeyupAutosave()"/>
                    <button mat-icon-button matSuffix [matTooltip]="'This is the internal identifier in the admin portal.'">
                        <mat-icon svgIcon="heroicons_outline:question-mark-circle"></mat-icon>
                    </button>
                </mat-form-field>

                <mat-form-field *ngIf="destination.id_destination && adminMode" appearance="outline" class="w-full md:w-1/2">
                    <mat-label>Destination ID</mat-label>
                    <input matInput readonly [value]="destination.id_destination" class="font-mono text-secondary">
                    <button mat-icon-button matSuffix [matTooltip]="'Unique system identifier. Use this ID for support requests.'">
                        <mat-icon svgIcon="heroicons_outline:question-mark-circle"></mat-icon>
                    </button>
                </mat-form-field>

                <div *ngIf="destination.ds_datalabel2 && userUiType === 4" class="mt-4 border-t pt-4">
                    <h3 class="text-lg font-medium mb-2">City Display Information</h3>
                    <p class="text-sm text-secondary mb-3">This will be displayed on the cities.</p>
                    <quill-editor (onEditorCreated)="onEditorCreated($event)" [readOnly]="reviewMode" (onContentChanged)="onKeyupAutosave()" class="w-full custom-quill" [(ngModel)]="destination.tx_data2" [bounds]="'self'" appEditableQuillHtml appEditableQuillHtmlButtonText="Source" [modules]="quillModules" [formats]="quillFormats"></quill-editor>
                </div>

            </mat-card-content>
        </mat-card>

        <mat-card appearance="outlined" *ngIf="adminMode || userUiType === 2 || userUiType === 4">
            <mat-card-header>
                <mat-card-title>Hierarchy & Type</mat-card-title>
                <mat-card-subtitle>Define where this destination fits within your structure.</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content class="pt-4">
                <div class="w-full mb-4">

                    <div *ngIf="isMismatchType()" class="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 p-4 mb-4 rounded shadow-sm text-sm">
                        <strong class="block text-base mb-1">⚠️ Type Update Required</strong>
                        The currently selected destination type <strong>({{ getOriginalTypeName(destination.id_destinationtype) }})</strong> is no longer supported. Please review and select a new destination type from the available options below.
                    </div>

                    <mat-form-field appearance="outline" class="w-full">
                        <mat-label>
                            Destination Type
                            <span *ngIf="isMissing(destination.id_destinationtype)" class="text-red-500 font-bold ml-1 animate-pulse">⚠️ Missing Critical Data</span>
                        </mat-label>

                        <mat-select *ngIf="userUiType !== 2 && userUiType !== 4" [disabled]="reviewMode" [(ngModel)]="destination.id_destinationtype" (selectionChange)="onDestinationTypeChange($event)">
                            <mat-option *ngFor="let stType of destinationTypesArray" [value]="stType.id_destinationtype">
                                {{ stType.ds_name }}
                            </mat-option>
                        </mat-select>

                        <mat-select *ngIf="userUiType === 2 || userUiType === 4" [disabled]="reviewMode || destination.id_destinationtype === '2a213080-4272-43b5-b0db-6d35bf1c0bec'" [(ngModel)]="destination.id_destinationtype" (selectionChange)="onDestinationTypeChange($event)">
                            <mat-select-trigger>
                                {{ getStaticTypeName(destination.id_destinationtype) || 'Select a Destination Type' }}
                            </mat-select-trigger>

                            <mat-option *ngIf="destination.id_destinationtype === '2a213080-4272-43b5-b0db-6d35bf1c0bec'" [value]="'2a213080-4272-43b5-b0db-6d35bf1c0bec'" class="h-auto py-3">
                                <div class="flex flex-col">
                                    <span class="font-semibold text-base">Ambassador</span>
                                </div>
                            </mat-option>

                            <mat-option *ngFor="let option of staticDestinationTypes" [value]="option.id_destinationtype" class="h-auto py-3">
                                <div class="flex flex-col">
                                    <span class="font-semibold text-base">{{ option.name }}</span>
                                    <span class="text-xs text-secondary whitespace-normal leading-tight mt-1">
                                        {{ option.examples.join(', ') }}
                                    </span>
                                </div>
                            </mat-option>
                        </mat-select>
                    </mat-form-field>
                </div>

                <div [ngClass]="(userUiType === 2 || userUiType === 4) ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-1 sm:grid-cols-3 gap-4'">
                    <mat-form-field appearance="outline" class="w-full" *ngIf="destination?.ggparent && userUiType !== 2 && userUiType !== 4">
                        <mat-label>{{ destination?.ggparent?.ds_destinationtype }}</mat-label>
                        <input matInput readonly [value]="destination?.ggparent?.ds_name" class="text-secondary">
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="w-full" *ngIf="destination?.gparent && userUiType !== 2 && userUiType !== 4">
                        <mat-label>{{ destination?.gparent?.ds_destinationtype }}</mat-label>
                        <input matInput readonly [value]="destination?.gparent?.ds_name" class="text-secondary">
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="w-full">
                        <mat-label>
                            <ng-container *ngIf="userUiType === 2 || userUiType === 4; else defaultParentLabel">
                                Kixa Location
                            </ng-container>
                            <ng-template #defaultParentLabel>
                                {{ destination?.parent?.ds_destinationtype ? destination?.parent?.ds_destinationtype : "Parent" }}
                            </ng-template>
                            <span *ngIf="isMissing(parentControl.value)" class="text-red-500 font-bold ml-1 animate-pulse">⚠️ Missing Critical Data</span>
                        </mat-label>
                        <input type="text" #autoCompleteTrigger="matAutocompleteTrigger" placeholder="Search for parent..." aria-label="Parent" matInput [formControl]="parentControl" [matAutocomplete]="auto" [readonly]="(!adminMode && userUiType !== 2 && userUiType !== 4) || reviewMode" (keyup)="onKeyupAutoSearch($event)" (blur)="onBlur()">
                        <mat-autocomplete #auto="matAutocomplete" [displayWith]="displayFn" (optionSelected)="onParentSelected($event)" requireSelection="true" autoActiveFirstOption="true">
                            <mat-option *ngFor="let city of filteredParents | async" [value]="city">
                                {{ city.ds_name }}
                            </mat-option>
                        </mat-autocomplete>
                        <mat-error *ngIf="parentCheckerRequired && parentControl.invalid && (parentControl.touched || parentControl.dirty)">
                            Please select a valid parent.
                        </mat-error>
                    </mat-form-field>
                </div>
            </mat-card-content>
        </mat-card>

        <mat-card appearance="outlined">
            <mat-card-header>
                <mat-card-title>Detailed Content</mat-card-title>
                <mat-card-subtitle>Rich text content for the destination page.</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content class="flex flex-col gap-8 pt-4">
                <div>
                    <h3 class="text-lg font-medium mb-2 flex items-center gap-2">
                        Description
                        <span *ngIf="isMissing(destination.tx_description)" class="bg-red-500 text-white px-2 py-0.5 rounded text-xs font-bold animate-pulse shadow-sm">
                            ⚠️ Missing Critical Data
                        </span>
                    </h3>
                    <p class="text-sm text-secondary mb-3">This text appears in the main body of the destination page.</p>
                    <quill-editor (onEditorCreated)="onEditorCreated($event)" [readOnly]="reviewMode" (onContentChanged)="onKeyupAutosave()" class="w-full custom-quill" [(ngModel)]="destination.tx_description" [bounds]="'self'" appEditableQuillHtml appEditableQuillHtmlButtonText="Source" [modules]="quillModules" [formats]="quillFormats"></quill-editor>
                </div>

                <div *ngIf="destination.ds_datalabel1">
                    <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 gap-2">
                        <div>
                            <ng-container *ngIf="userUiType === 4; else defaultLabel1">
                                <h3 class="text-lg font-medium">Ambassador Personal Details</h3>
                                <p class="text-sm text-secondary">This will be in the personal ambassador page.</p>
                            </ng-container>
                            <ng-template #defaultLabel1>
                                <h3 class="text-lg font-medium">{{ destination.ds_datalabel1 }}</h3>
                                <p class="text-sm text-secondary">Additional information section.</p>
                            </ng-template>
                        </div>
                        <button *ngIf="isBusinessHours" mat-stroked-button (click)="toggleEditorForData1()" [matTooltip]="!showRawEditorForData1 ? 'Switch to Rich Text Editor' : 'Use Business Hours Editor'">
                            <mat-icon svgIcon="heroicons_outline:arrows-right-left" class="mr-2"></mat-icon>
                            <span>Toggle Editor</span>
                        </button>
                    </div>

                    <ng-container [ngSwitch]="destination.ds_datalabel1">
                        <ng-container *ngSwitchCase="'Hours'">
                            <ng-container *ngIf="!showRawEditorForData1; else quillEditorForData1">
                                <app-opening-hours-input class="block" [value]="openingHours" (valueChange)="handleHoursChange($event)"></app-opening-hours-input>
                            </ng-container>
                        </ng-container>

                        <ng-container *ngSwitchCase="'SocialNetworks'">
                            <app-social-links-editor [(ngModel)]="destination.tx_data1" (ngModelChange)="onKeyupAutosave()"></app-social-links-editor>
                        </ng-container>

                        <ng-container *ngSwitchDefault>
                            <ng-container *ngTemplateOutlet="quillEditorForData1"></ng-container>
                        </ng-container>
                    </ng-container>

                    <ng-template #quillEditorForData1>
                        <quill-editor (onEditorCreated)="onEditorCreated($event)" [readOnly]="reviewMode" (onContentChanged)="onKeyupAutosave()" class="w-full custom-quill" [(ngModel)]="destination.tx_data1" [bounds]="'self'" appEditableQuillHtml appEditableQuillHtmlButtonText="Source" [modules]="quillModules" [formats]="quillFormats"></quill-editor>
                    </ng-template>
                </div>

                <div *ngIf="destination.ds_datalabel2 && userUiType !== 4">
                    <h3 class="text-lg font-medium mb-2">{{ destination.ds_datalabel2 }}</h3>
                    <p class="text-sm text-secondary mb-3">Additional information section.</p>
                    <quill-editor (onEditorCreated)="onEditorCreated($event)" [readOnly]="reviewMode" (onContentChanged)="onKeyupAutosave()" class="w-full custom-quill" [(ngModel)]="destination.tx_data2" [bounds]="'self'" appEditableQuillHtml appEditableQuillHtmlButtonText="Source" [modules]="quillModules" [formats]="quillFormats"></quill-editor>
                </div>

                <div *ngIf="destination.ds_datalabel3">
                    <h3 class="text-lg font-medium mb-2">{{ destination.ds_datalabel3 }}</h3>
                    <p class="text-sm text-secondary mb-3">Additional information section.</p>
                    <quill-editor (onEditorCreated)="onEditorCreated($event)" [readOnly]="reviewMode" (onContentChanged)="onKeyupAutosave()" class="w-full custom-quill" [(ngModel)]="destination.tx_data3" [bounds]="'self'" appEditableQuillHtml appEditableQuillHtmlButtonText="Source" [modules]="quillModules" [formats]="quillFormats"></quill-editor>
                </div>
            </mat-card-content>
        </mat-card>
    </div>

    <div class="lg:col-span-1 flex flex-col gap-6">
        <mat-card appearance="outlined">
            <mat-card-header>
                <mat-card-title>Contact Information</mat-card-title>
                <mat-card-subtitle>How users can get in touch.</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content class="flex flex-col gap-4 pt-4">
                <mat-form-field appearance="outline" class="w-full">
                    <mat-label>Email</mat-label>
                    <input matInput type="email" maxlength="150" [readonly]="reviewMode" (keyup)="onKeyupAutosave()" [(ngModel)]="destination.ds_email">
                </mat-form-field>

                <div class="w-full">
                    <label class="block text-sm font-medium text-secondary mb-1">Phone Number</label>
                    <app-phone-input [readonly]="reviewMode" [(ngModel)]="destination.ds_phone" (ngModelChange)="onKeyupAutosave()"></app-phone-input>
                </div>

                <mat-form-field appearance="outline" class="w-full">
                    <mat-label>Website</mat-label>
                    <input matInput type="url" placeholder="https://example.com" maxlength="255" [readonly]="reviewMode" (keyup)="onKeyupAutosave()" [(ngModel)]="destination.ds_website">
                </mat-form-field>
            </mat-card-content>
        </mat-card>
    </div>

</div>
